import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch, RootState } from "./store";

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */

export type OrderStatus =
  | "pending"
  | "confirmed"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "refunded"
  | "disputed";

export interface GlamourOrderItem {
  listingId:   string;
  hostId:      string;
  brandId?:    string;
  brandName?:  string;

  name:        string;
  image?:      string;
  rate:        number;
  qty:         number;
  size?:       string;
  colour?:     string;
  sku?:        string;

  subtotal:    number;

  deliveryStatus: {
    sellerDelivered: boolean;  // brand marks as shipped
    buyerReceived:   boolean;  // buyer confirms receipt
    orderDelivered:  boolean;  // both sides confirmed → true
  };

  // Per-item tracking
  trackingNumber?: string;
  trackingUrl?:    string;
  carrier?:        string;
  shippedAt?:      string | null;
  deliveredAt?:    string | null;
}

export interface GlamourShippingAddress {
  fullName:    string;
  phone:       string;
  street:      string;
  city:        string;
  county?:     string;
  country:     string;
  zipCode?:    string;
  notes?:      string;
  latitude?:   number | null;
  longitude?:  number | null;
}

export interface GlamourOrder {
  id:       string;
  guestId:  string; // buyer userId
  hostId:   string; // primary seller (first item's hostId)

  items:    GlamourOrderItem[];

  // Financials
  subtotal:        number;
  deliveryFee:     number;
  discount:        number;
  totalQty:        number;
  totalPaid:       number;
  currency:        string;

  guestsFee:       number;   // buyer service fee
  hostsFee:        number;   // seller commission
  hostProfit:      number;   // seller net
  platformProfit:  number;   // Glamour net

  // Payment
  paymentId:       string;
  paymentMethod?:  string;
  paymentStatus?:  "pending" | "paid" | "failed" | "refunded";

  // Delivery
  shippingAddress?: GlamourShippingAddress;
  deliveryZone?:    string;

  // Status flags
  status:          OrderStatus;
  isConfirmed:     boolean;
  isCancelled:     boolean;
  isRefunded:      boolean;
  isPaid:          boolean;
  isDelivered:     boolean;
  isDisputed?:     boolean;

  // Cancellation / refund
  cancelReason?:   string;
  refundReason?:   string;
  refundAmount?:   number;

  // Timestamps
  createdAt?: number | null;
  updatedAt?: number | null;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
  deliveredAt?: string | null;

  // Review flag
  reviewSubmitted?: boolean;

  [key: string]: any;
}

interface GlamourOrdersState {
  orders:        GlamourOrder[];
  currentOrder:  GlamourOrder | null;
  loading:       boolean;
  error:         string | null;
  unsubscribe?:  (() => void) | undefined;

  // Host analytics
  hostStats: {
    total:     number;
    delivered: number;
    pending:   number;
    cancelled: number;
    profit:    number;
    revenue:   number;
  };
}

/* ═══════════════════════════════════════════════════════════
   INITIAL STATE
═══════════════════════════════════════════════════════════ */

const initialState: GlamourOrdersState = {
  orders:       [],
  currentOrder: null,
  loading:      false,
  error:        null,
  unsubscribe:  undefined,
  hostStats: {
    total:     0,
    delivered: 0,
    pending:   0,
    cancelled: 0,
    profit:    0,
    revenue:   0,
  },
};

const COLLECTION = "glamourOrders"; // 🔑 Glamour collection name

/* ═══════════════════════════════════════════════════════════
   PARSER — raw Firestore doc → GlamourOrder
═══════════════════════════════════════════════════════════ */

const parseOrder = (id: string, raw: Record<string, any>): GlamourOrder => ({
  id,
  guestId:  raw.guestId  ?? "",
  hostId:   raw.hostId   ?? "",

  items: Array.isArray(raw.items)
    ? raw.items.map((item: any) => ({
        listingId:   item.listingId   ?? "",
        hostId:      item.hostId      ?? "",
        brandId:     item.brandId     ?? "",
        brandName:   item.brandName   ?? "",
        name:        item.name        ?? "",
        image:       item.image       ?? "",
        rate:        item.rate        ?? 0,
        qty:         item.qty         ?? 1,
        size:        item.size        ?? "",
        colour:      item.colour      ?? "",
        sku:         item.sku         ?? "",
        subtotal:    item.subtotal    ?? (item.rate ?? 0) * (item.qty ?? 1),
        deliveryStatus: {
          sellerDelivered: item.deliveryStatus?.sellerDelivered ?? false,
          buyerReceived:   item.deliveryStatus?.buyerReceived   ?? false,
          orderDelivered:  item.deliveryStatus?.orderDelivered  ?? false,
        },
        trackingNumber: item.trackingNumber ?? "",
        trackingUrl:    item.trackingUrl    ?? "",
        carrier:        item.carrier        ?? "",
        shippedAt:      item.shippedAt      ?? null,
        deliveredAt:    item.deliveredAt    ?? null,
      }))
    : [],

  subtotal:       raw.subtotal       ?? 0,
  deliveryFee:    raw.deliveryFee    ?? 0,
  discount:       raw.discount       ?? 0,
  totalQty:       raw.totalQty       ?? 0,
  totalPaid:      raw.totalPaid      ?? 0,
  currency:       raw.currency       ?? "EUR",

  guestsFee:      raw.guestsFee      ?? 0,
  hostsFee:       raw.hostsFee       ?? 0,
  hostProfit:     raw.hostProfit     ?? 0,
  platformProfit: raw.platformProfit ?? 0,

  paymentId:      raw.paymentId      ?? "",
  paymentMethod:  raw.paymentMethod  ?? "",
  paymentStatus:  raw.paymentStatus  ?? "pending",

  shippingAddress: raw.shippingAddress ?? null,
  deliveryZone:    raw.deliveryZone    ?? "",

  status:        raw.status        ?? "pending",
  isConfirmed:   raw.isConfirmed   ?? false,
  isCancelled:   raw.isCancelled   ?? false,
  isRefunded:    raw.isRefunded    ?? false,
  isPaid:        raw.isPaid        ?? false,
  isDelivered:   raw.isDelivered   ?? false,
  isDisputed:    raw.isDisputed    ?? false,

  cancelReason:  raw.cancelReason  ?? "",
  refundReason:  raw.refundReason  ?? "",
  refundAmount:  raw.refundAmount  ?? 0,

  createdAt:     raw.createdAt?.toMillis?.()  ?? null,
  updatedAt:     raw.updatedAt?.toMillis?.()  ?? null,
  confirmedAt:   raw.confirmedAt  ?? null,
  cancelledAt:   raw.cancelledAt  ?? null,
  deliveredAt:   raw.deliveredAt  ?? null,

  reviewSubmitted: raw.reviewSubmitted ?? false,
});

/* ═══════════════════════════════════════════════════════════
   UPSERT ORDER  — update delivery status, cancel, refund, etc.
═══════════════════════════════════════════════════════════ */

export const upsertGlamourOrder = createAsyncThunk<
  GlamourOrder,
  Partial<GlamourOrder> & {
    paymentId: string;
    hostId?: string;
    listingId?: string;
    trackingInfo?: {
      trackingNumber?: string;
      trackingUrl?: string;
      carrier?: string;
    };
    deliveryStatus?: {
      sellerDelivered?: boolean;
      buyerReceived?: boolean;
    };
    cancelReason?: string;
    refundReason?: string;
    refundAmount?: number;
  },
  { rejectValue: string }
>(
  "glamourOrders/upsert",
  async (orderData, { rejectWithValue }) => {
    try {
      if (!orderData.paymentId) throw new Error("Missing paymentId");

      const orderId  = orderData.paymentId;
      const docRef   = doc(db, COLLECTION, orderId);
      const snap     = await getDoc(docRef);

      if (!snap.exists()) throw new Error("Order not found");

      const existing = parseOrder(snap.id, snap.data());
      const payload: any = { updatedAt: serverTimestamp() };

      /* ── Update delivery status per item ── */
      if (orderData.deliveryStatus) {
        const updatedItems = existing.items.map((item) => {
          // Seller marks as shipped
          if (
            orderData.hostId &&
            item.hostId === orderData.hostId &&
            orderData.deliveryStatus?.sellerDelivered === true
          ) {
            return {
              ...item,
              deliveryStatus: {
                ...item.deliveryStatus,
                sellerDelivered: true,
              },
              shippedAt: new Date().toISOString(),
              ...(orderData.trackingInfo ?? {}),
            };
          }

          // Buyer confirms receipt
          if (
            orderData.listingId &&
            item.listingId === orderData.listingId &&
            orderData.deliveryStatus?.buyerReceived === true
          ) {
            return {
              ...item,
              deliveryStatus: {
                ...item.deliveryStatus,
                buyerReceived:  true,
                orderDelivered: true,
              },
              deliveredAt: new Date().toISOString(),
            };
          }

          return item;
        });

        payload.items = updatedItems;

        // If all items are fully delivered → mark whole order delivered
        const allDelivered = updatedItems.every(
          (i) =>
            i.deliveryStatus.sellerDelivered === true &&
            i.deliveryStatus.buyerReceived   === true
        );

        if (allDelivered) {
          payload.isDelivered  = true;
          payload.status       = "delivered";
          payload.deliveredAt  = new Date().toISOString();
        }
      }

      /* ── Cancel ── */
      if (orderData.isCancelled === true) {
        payload.isCancelled  = true;
        payload.status       = "cancelled";
        payload.cancelledAt  = new Date().toISOString();
        payload.cancelReason = orderData.cancelReason ?? "";
      }

      /* ── Refund ── */
      if (orderData.isRefunded === true) {
        payload.isRefunded   = true;
        payload.status       = "refunded";
        payload.refundReason = orderData.refundReason ?? "";
        payload.refundAmount = orderData.refundAmount ?? existing.totalPaid;
      }

      /* ── Dispute ── */
      if (orderData.isDisputed === true) {
        payload.isDisputed = true;
        payload.status     = "disputed";
      }

      /* ── Generic field update ── */
      const safeFields = [
        "isConfirmed", "isPaid", "paymentStatus",
        "shippingAddress", "deliveryZone", "reviewSubmitted",
      ] as const;

      safeFields.forEach((field) => {
        if ((orderData as any)[field] !== undefined) {
          payload[field] = (orderData as any)[field];
        }
      });

      if (orderData.isConfirmed === true && !existing.isConfirmed) {
        payload.confirmedAt = new Date().toISOString();
        payload.status      = "confirmed";
      }

      await updateDoc(docRef, payload);

      return parseOrder(orderId, { ...snap.data(), ...payload });
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to upsert order");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   LISTEN TO SINGLE ORDER  (real-time)
═══════════════════════════════════════════════════════════ */

export const listenToGlamourOrder =
  (orderId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, COLLECTION, orderId);

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          dispatch(
            glamourOrdersSlice.actions.setCurrentOrder(
              parseOrder(snap.id, snap.data())
            )
          );
        } else {
          dispatch(glamourOrdersSlice.actions.setCurrentOrder(null));
        }
      },
      (error) => dispatch(glamourOrdersSlice.actions.setError(error.message))
    );

    dispatch(glamourOrdersSlice.actions.setUnsubscribe(unsubscribe));
    return unsubscribe;
  };

/* ═══════════════════════════════════════════════════════════
   LISTEN TO GUEST (BUYER) ORDERS  (real-time)
═══════════════════════════════════════════════════════════ */

export const listenToGlamourGuestOrders =
  (guestId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!guestId) return;

    const { unsubscribe } = getState().glamourOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, COLLECTION),
      where("guestId", "==", guestId),
      orderBy("createdAt", "desc")
    );

    const unsubFn = onSnapshot(
      q,
      (snap) => {
        dispatch(
          glamourOrdersSlice.actions.setOrders(
            snap.docs.map((d) => parseOrder(d.id, d.data()))
          )
        );
      },
      (error) => dispatch(glamourOrdersSlice.actions.setError(error.message))
    );

    dispatch(glamourOrdersSlice.actions.setUnsubscribe(unsubFn));
    return unsubFn;
  };

/* ═══════════════════════════════════════════════════════════
   LISTEN TO HOST (SELLER) ORDERS  — only their items, real-time
═══════════════════════════════════════════════════════════ */

export const listenToGlamourHostOrders =
  (hostId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().glamourOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, COLLECTION),
      where("isConfirmed", "==", true),
      orderBy("createdAt", "desc")
    );

    const unsubFn = onSnapshot(
      q,
      (snap) => {
        const orders: GlamourOrder[] = snap.docs
          .map((d) => {
            const order     = parseOrder(d.id, d.data());
            const hostItems = order.items.filter((i) => i.hostId === hostId);
            if (!hostItems.length) return null;

            // Calculate this seller's slice of financials
            const hostSubtotal = hostItems.reduce((s, i) => s + i.subtotal, 0);

            return { ...order, items: hostItems, subtotal: hostSubtotal };
          })
          .filter(Boolean) as GlamourOrder[];

        dispatch(glamourOrdersSlice.actions.setOrders(orders));
      },
      (error) => dispatch(glamourOrdersSlice.actions.setError(error.message))
    );

    dispatch(glamourOrdersSlice.actions.setUnsubscribe(unsubFn));
    return unsubFn;
  };

/* ═══════════════════════════════════════════════════════════
   FETCH HOST STATS  (one-off analytics)
═══════════════════════════════════════════════════════════ */

export const fetchGlamourHostStats = createAsyncThunk<
  GlamourOrdersState["hostStats"],
  string,
  { rejectValue: string }
>(
  "glamourOrders/fetchHostStats",
  async (hostId, { rejectWithValue }) => {
    try {
      if (!hostId) throw new Error("Missing hostId");

      const q    = query(collection(db, COLLECTION), where("isConfirmed", "==", true));
      const snap = await getDocs(q);

      let total = 0, delivered = 0, pending = 0, cancelled = 0, profit = 0, revenue = 0;

      snap.forEach((d) => {
        const order     = parseOrder(d.id, d.data());
        const hostItems = order.items.filter((i) => i.hostId === hostId);
        if (!hostItems.length) return;

        total++;
        revenue += hostItems.reduce((s, i) => s + i.subtotal, 0);

        if (order.isDelivered)       { delivered++; profit += order.hostProfit; }
        else if (order.isCancelled)  { cancelled++; }
        else                          { pending++; }
      });

      return { total, delivered, pending, cancelled, profit, revenue };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch host stats");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH RECENT ORDERS  (admin dashboard, paginated)
═══════════════════════════════════════════════════════════ */

export const fetchRecentGlamourOrders = createAsyncThunk<
  GlamourOrder[],
  { limitCount?: number },
  { rejectValue: string }
>(
  "glamourOrders/fetchRecent",
  async ({ limitCount = 50 }, { rejectWithValue }) => {
    try {
      const q    = query(collection(db, COLLECTION), orderBy("createdAt", "desc"), limit(limitCount));
      const snap = await getDocs(q);
      return snap.docs.map((d) => parseOrder(d.id, d.data()));
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch recent orders");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════ */

const glamourOrdersSlice = createSlice({
  name: "glamourOrders",
  initialState,
  reducers: {
    setOrders: (state, action: PayloadAction<GlamourOrder[]>) => {
      state.orders = action.payload;
    },
    setCurrentOrder: (state, action: PayloadAction<GlamourOrder | null>) => {
      state.currentOrder = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    clearOrders: (state) => {
      state.orders       = [];
      state.currentOrder = null;
      state.error        = null;
    },
    unsubscribeOrders: (state) => {
      state.unsubscribe?.();
      state.unsubscribe = undefined;
    },
  },
  extraReducers: (builder) => {
    /* ── upsert ── */
    builder.addCase(upsertGlamourOrder.fulfilled, (state, action) => {
      const idx = state.orders.findIndex((o) => o.id === action.payload.id);
      if (idx >= 0)                state.orders[idx] = action.payload;
      else                         state.orders.unshift(action.payload);
      if (state.currentOrder?.id === action.payload.id) {
        state.currentOrder = action.payload;
      }
      state.loading = false;
    });

    builder.addCase(upsertGlamourOrder.rejected, (state, action) => {
      state.error   = action.payload ?? "Failed to update order";
      state.loading = false;
    });

    /* ── host stats ── */
    builder.addCase(fetchGlamourHostStats.fulfilled, (state, action) => {
      state.hostStats = action.payload;
      state.loading   = false;
    });

    /* ── recent orders ── */
    builder.addCase(fetchRecentGlamourOrders.fulfilled, (state, action) => {
      state.orders  = action.payload;
      state.loading = false;
    });

    /* ── global pending / rejected ── */
    builder.addMatcher(
      (a) => a.type.startsWith("glamourOrders/") && a.type.endsWith("/pending"),
      (state) => {
        state.loading = true;
        state.error   = null;
      }
    );
    builder.addMatcher(
      (a) => a.type.startsWith("glamourOrders/") && a.type.endsWith("/rejected"),
      (state, a: any) => {
        state.loading = false;
        state.error   = a.payload ?? a.error?.message ?? "Something went wrong";
      }
    );
  },
});

export const {
  setOrders,
  setCurrentOrder,
  setError,
  setUnsubscribe,
  clearOrders,
  unsubscribeOrders,
} = glamourOrdersSlice.actions;

export default glamourOrdersSlice.reducer;